import { ReactNode, useState } from 'react';
import useResizeObserver from 'use-resize-observer';

import { T } from '@oaknorth/util-intl';

import { Box, Button, Presence } from '../../chakra';

export interface ExpandableSectionProps {
  children: ReactNode;
  collapsedHeight?: number;
  id?: string;
}

const BOTTOM_EDGE_FADE = 'linear-gradient(to top, transparent 0, black 40px)';
const HEIGHT_TOLERANCE = 14;

export function useExpandableSection({ collapsedHeight }: { collapsedHeight: number }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const contentBox = useResizeObserver();

  // Until we render content, assume it's too long
  const contentIsTooLong = (contentBox.height ?? Number.MAX_SAFE_INTEGER) > collapsedHeight + HEIGHT_TOLERANCE;
  const heightIsSet = contentBox.height != null;

  // Only show controls in second render in case we don't need them
  // Late render is better than flash
  const shouldShowControls = contentIsTooLong && heightIsSet;

  return {
    toggleExpanded: () => setIsExpanded((prev) => !prev),
    expandSection: () => setIsExpanded(true),
    collapseSection: () => setIsExpanded(false),
    // Should be applied to a div which is the immediate child of the `Collapse`, wrapping the content
    contentBoxRef: contentBox.ref,
    // If the collapse should be expanded
    isExpanded: isExpanded || !contentIsTooLong,
    // Whether we should show controls - only if we have enough content to overflow
    shouldShowControls,
  };
}

export function ExpandableSection({
  children,
  collapsedHeight = 150,
  id = 'expandable-section',
}: ExpandableSectionProps) {
  const { contentBoxRef, isExpanded, shouldShowControls, toggleExpanded } = useExpandableSection({
    collapsedHeight,
  });

  return (
    <Box>
      <Box
        css={
          isExpanded
            ? undefined
            : {
                // Provides a fade on the bottom edge
                // The black isn't visible as mask-image only applies to the alpha channel
                // Only supported in chrome with vendor prefix
                webkitMaskImage: BOTTOM_EDGE_FADE,
                maskImage: BOTTOM_EDGE_FADE,
              }
        }
      >
        <Presence
          present={true}
          height={isExpanded ? undefined : collapsedHeight}
          animationName={isExpanded ? 'slide-from-bottom' : 'slide-to-bottom'}
        >
          <Box ref={contentBoxRef}>{children}</Box>
        </Presence>
      </Box>

      {shouldShowControls ? (
        <Button
          variant="ghost"
          onClick={toggleExpanded}
          data-testid={`${id}-${isExpanded ? 'read-less' : 'read-more'}`}
        >
          {isExpanded ? T`expandableSection.readLess.label` : T`expandableSection.readMore.label`}
        </Button>
      ) : null}
    </Box>
  );
}
